import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthResponse } from '../types';
import {
  getToken,
  getUser,
  setToken,
  setRefreshToken,
  setUser,
  clearAuth
} from '../utils/auth/tokenStorage';
import { updateSocketAuth } from '../services/socket/socketClient';
import * as authApi from '../services/api/authApi';

// Initial state
const initialState: AuthState = {
  isAuthenticated: Boolean(getToken()),
  user: getUser(),
  isLoading: false,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: AuthResponse }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'REFRESH_USER_START' }
  | { type: 'REFRESH_USER_SUCCESS'; payload: AuthResponse['user'] }
  | { type: 'REFRESH_USER_FAILURE'; payload: string }
  | { type: 'AUTH_LOADING' }; // New action type

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return {
        ...state,
        isLoading: true,
      };
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isAuthenticated: false,
        user: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'REFRESH_USER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'REFRESH_USER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        error: null,
      };
    case 'REFRESH_USER_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

// Context interface
interface AuthContextType extends AuthState {
  login: (phone: string) => Promise<boolean>;
  verifyOtp: (phone: string, otp: string, isSignup?: boolean, name?: string) => Promise<boolean>;
  signup: (name: string, phone: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  refreshUserData: () => Promise<boolean>;
  isAdmin: boolean; // Added admin property
}

// Create context
export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  login: async () => false,
  verifyOtp: async () => false,
  signup: async () => false,
  logout: () => {},
  clearError: () => {},
  refreshUserData: async () => false,
  isAdmin: false, // Default value for isAdmin
});

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if current user is an admin
  const isAdmin = state.user?.role === 'admin';

  // Initialize socket with token when authenticated
  useEffect(() => {
    const token = getToken();
    if (token) {
      updateSocketAuth(token);
    }
  }, [state.isAuthenticated]);

  // Auto-fetch user data if authenticated but no user data or missing important fields
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      const user = getUser();
      
      // If we have a token but no user data or missing meetingPasses, fetch user data
      if (token && (!user?._id || !user?.meetingPasses)) {
        dispatch({ type: 'AUTH_LOADING' });
        console.log('Token exists but user data missing or incomplete. Fetching user data...');
        try {
          const userData = await authApi.getCurrentUser();
          console.log('User data fetched successfully:', userData);
          setUser(userData);
          dispatch({ type: 'REFRESH_USER_SUCCESS', payload: userData });
        } catch (error: any) {
          console.error('Failed to fetch user data:', error);
          dispatch({ type: 'REFRESH_USER_FAILURE', payload: 'Failed to load user data' });
        }
      }
    };
    
    initAuth();
  }, []);  

  // Login function
  const login = async (phone: string): Promise<boolean> => {
    try {
      dispatch({ type: 'AUTH_START' });
      console.log('Sending login request for phone:', phone);
      await authApi.login({ phone });
      console.log('Login request successful, OTP sent');
      return true;
    } catch (error: any) {
      // Get the error message directly from the Error object
      const message = error.message || 'Failed to send OTP';
      console.error('Login error:', message, error);
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      throw error; // Re-throw error so LoginForm can display it
      return false; // This line won't be reached due to throw above
    }
  };

  // Verify OTP function
  const verifyOtp = async (
    phone: string, 
    otp: string, 
    isSignup: boolean = false,
    name?: string
  ): Promise<boolean> => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Log the data being sent to the API
      console.log('Verifying OTP with:', {
        phone,
        otp,
        isSignup,
        name
      });
      
      // Make sure OTP is sent as a string
      const response = await authApi.verifyOtp({ 
        phone, 
        otp: String(otp), 
        isSignup, 
        name 
      });
      
      console.log('OTP verification successful:', response);
      
      setToken(response.token);
      setRefreshToken(response.refreshToken);
      setUser(response.user);
      updateSocketAuth(response.token);
      
      dispatch({ type: 'AUTH_SUCCESS', payload: response });
      
      // IMPORTANT: Immediately fetch complete user data after successful OTP verification
      // This ensures we have all user data including meetingPasses
      try {
        console.log('Fetching complete user data after login...');
        const userData = await authApi.getCurrentUser();
        console.log('Complete user data fetched:', userData);
        
        // Update local storage and state with complete user data
        setUser(userData);
        dispatch({ type: 'REFRESH_USER_SUCCESS', payload: userData });
      } catch (error) {
        console.error('Failed to fetch complete user data after login:', error);
        // Don't reject the login if this fails, we still have basic user data
      }
      
      return true;
    } catch (error: any) {
      // Get the error message directly from the Error object
      const message = error.message || 'Invalid OTP';
      console.error('OTP verification error:', message, error);
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      throw error; // Re-throw error so it can be displayed
      return false; // This line won't be reached due to throw above
    }
  };

  // Signup function
  const signup = async (name: string, phone: string): Promise<boolean> => {
    try {
      dispatch({ type: 'AUTH_START' });
      console.log('Sending signup request for:', { name, phone });
      await authApi.signup({ name, phone });
      console.log('Signup request successful, OTP sent');
      return true;
    } catch (error: any) {
      // Get the error message directly from the Error object
      const message = error.message || 'Signup failed';
      console.error('Signup error:', message, error);
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      throw error; // Re-throw error so it can be displayed
      return false; // This line won't be reached due to throw above
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      // Reset Crisp session if it exists
      if (window.$crisp) {
        window.$crisp.push(["do", "session:reset"]);
      }
      
      clearAuth();
      updateSocketAuth(null);
      dispatch({ type: 'LOGOUT' });
    }
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Refresh user data function
  const refreshUserData = async (): Promise<boolean> => {
    try {
      dispatch({ type: 'REFRESH_USER_START' });
      console.log('Refreshing user data');
      
      const userData = await authApi.getCurrentUser();
      console.log('User data refreshed successfully:', userData);
      
      // Update local storage
      setUser(userData);
      
      dispatch({ type: 'REFRESH_USER_SUCCESS', payload: userData });
      return true;
    } catch (error: any) {
      // Get the error message directly from the Error object
      const message = error.message || 'Failed to refresh user data';
      console.error('Refresh user data error:', message, error);
      dispatch({ type: 'REFRESH_USER_FAILURE', payload: message });
      throw error; // Re-throw error so it can be displayed
      return false; // This line won't be reached due to throw above
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        verifyOtp,
        signup,
        logout,
        clearError,
        refreshUserData,
        isAdmin, // Add isAdmin to the context value
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
